<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetFixedAssetWasteBin',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    fixedAsset: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const binTypes = ref([
    {
      id: 'communal',
      name: $fn.value.pgettext('BinTypes', 'Communal'),
    },
    {
      id: 'paper',
      name: $fn.value.pgettext('BinTypes', 'Paper'),
    },
    {
      id: 'plastic',
      name: $fn.value.pgettext('BinTypes', 'Plastic'),
    },
  ]);
  const ux = ref([]);

  const $data = computed(() => ({
    binTypes: binTypes.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'binTypes' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { binTypes, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.fixedAsset,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('fixedAsset');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  const act_7315092382398562_reloadComponentData_a039zvsd7 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_g5ztb3py7 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_a039zvsd7,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_g5ztb3py7],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_g5ztb3py7,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_g5ztb3py7 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_g5ztb3py7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="bin_type"
      :label="pgettext('FixedAssetWasteBin', 'Type')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormSearchableSelect
        name="groups"
        size="md"
        v-model="fixedAsset.waste_bin.bin_type"
        :options="binTypes"
        valueField="id"
        textField="name"
        required
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="bin_number"
      :label="pgettext('FixedAssetWasteBin', 'Number')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput
        type="number"
        name="bin_number"
        v-model="fixedAsset.waste_bin.bin_number"
        required
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="capacity"
      :label="pgettext('FixedAssetWasteBin', 'Capacity')"
      :labelAlign="labelAlign"
      :appendText="pgettext('InputAddon', 'liter')"
      required
    >
      <ItpFormInput
        type="number"
        name="capacity"
        v-model="fixedAsset.waste_bin.capacity.amount"
        required
      >
      </ItpFormInput>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
